import React, { createContext, useContext, useMemo, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import { loadStripeWithRetry, resetStripeLoader } from './stripeLoader';
import { trackStripeRetryClicked } from './analytics/funnelV3Analytics';

/**
 * Call this to start downloading Stripe.js early (e.g. on paywall mount).
 * Safe to call multiple times — only triggers one download.
 */
export function preloadStripe(): void {
    loadStripeWithRetry(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');
}

// Context so child components can check if Elements is already provided
// and whether it was initialized with deferred-payment options (mode/amount/currency)
// needed by ExpressCheckoutElement.
interface StripeReadyContextValue {
    ready: boolean;
    hasExpressCheckoutOptions: boolean;
}

const StripeReadyContext = createContext<StripeReadyContextValue>({
    ready: false,
    hasExpressCheckoutOptions: false,
});

export function useStripeReady(): StripeReadyContextValue {
    return useContext(StripeReadyContext);
}

/**
 * Wrap any subtree that needs Stripe Elements.
 * Uses the shared singleton — no duplicate downloads.
 */
interface StripeProviderProps {
    children: React.ReactNode;
    options?: StripeElementsOptions;
}

interface StripeLoadFailedFallbackProps {
    onRetry: () => void;
}

const StripeLoadFailedFallback: React.FC<StripeLoadFailedFallbackProps> = ({ onRetry }) => (
    <div style={{ padding: '24px', textAlign: 'center', color: '#333' }}>
        <p style={{ fontSize: '16px', marginBottom: '12px' }}>
            We're having trouble loading the payment form.
        </p>
        <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
            Please check your connection and try again.
        </p>
        <button
            onClick={onRetry}
            style={{
                padding: '12px 24px',
                background: '#000',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
            }}
        >
            Try again
        </button>
    </div>
);

export const StripeProvider: React.FC<StripeProviderProps> = ({ children, options }) => {
    const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    const [retryKey, setRetryKey] = useState(0);
    const [loadFailed, setLoadFailed] = useState(false);

    const promise = useMemo(() => {
        return loadStripeWithRetry(key || '').catch((err) => {
            setLoadFailed(true);
            throw err;
        });
        // retryKey is a bump trigger: incrementing it via setRetryKey in handleRetry
        // forces a fresh loadStripeWithRetry call after resetStripeLoader().
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, retryKey]);

    if (!key) {
        console.error('[StripeProvider] Missing REACT_APP_STRIPE_PUBLISHABLE_KEY');
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <p>Payment system unavailable. Please contact support.</p>
            </div>
        );
    }

    const handleRetry = () => {
        trackStripeRetryClicked();
        resetStripeLoader();
        setLoadFailed(false);
        setRetryKey((prev) => prev + 1);
    };

    if (loadFailed) {
        return <StripeLoadFailedFallback onRetry={handleRetry} />;
    }

    const hasExpressCheckoutOptions = !!options && 'mode' in options && !!options.mode;

    return (
        <StripeReadyContext.Provider value={{ ready: true, hasExpressCheckoutOptions }}>
            <Elements stripe={promise} options={options}>
                {children}
            </Elements>
        </StripeReadyContext.Provider>
    );
};
